/**
 * Spec §10 — customer live Delay / Pause / no-show notices when DTO facts are present.
 */
import { useTranslation } from 'react-i18next';
import type {
  OrderLiveDelayNotice,
  OrderLiveNoShowNotice,
  OrderLiveOpsNotice,
} from '../hooks/useOrderLiveStatus.js';

export interface OrderLiveNoticesPanelProps {
  readonly delayNotice: OrderLiveDelayNotice | null;
  readonly opsNotice: OrderLiveOpsNotice | null;
  readonly noShowNotice: OrderLiveNoShowNotice | null;
  readonly formatDate: (iso: string) => string;
}

export function OrderLiveNoticesPanel({
  delayNotice,
  opsNotice,
  noShowNotice,
  formatDate,
}: OrderLiveNoticesPanelProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  if (delayNotice == null && opsNotice == null && noShowNotice == null) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3" data-testid="order-live-notices">
      {delayNotice != null ? (
        <section
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
          data-testid="order-live-delay-notice"
        >
          <p className="m-0 text-sm font-medium text-[var(--color-on-surface)]">
            {t('account.orders.live.delayTitle', { defaultValue: 'Pickup time updated' })}
          </p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface-muted)]">{delayNotice.apology}</p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface)]">
            {t('account.orders.live.newEta', {
              defaultValue: 'New pickup time: {{at}}',
              at: formatDate(delayNotice.promisedPickupAt),
            })}
          </p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface-muted)]">{delayNotice.nextStep}</p>
        </section>
      ) : null}
      {opsNotice != null ? (
        <section
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
          data-testid="order-live-ops-notice"
        >
          <p className="m-0 text-sm font-medium text-[var(--color-on-surface)]">
            {opsNotice.mode === 'PAUSE'
              ? t('account.orders.live.pauseTitle', { defaultValue: 'Shop pause' })
              : t('account.orders.live.delayOpsTitle', { defaultValue: 'Delayed operations' })}
          </p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface-muted)]">{opsNotice.impact}</p>
          {opsNotice.opsEndsAt != null ? (
            <p className="mt-1 m-0 text-sm text-[var(--color-on-surface-muted)]">
              {t('account.orders.live.opsEndsAt', {
                defaultValue: 'Expected until: {{at}}',
                at: formatDate(opsNotice.opsEndsAt),
              })}
            </p>
          ) : null}
        </section>
      ) : null}
      {noShowNotice != null ? (
        <section
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
          data-testid="order-live-noshow-notice"
        >
          <p className="m-0 text-sm font-medium text-[var(--color-on-surface)]">
            {t('account.orders.live.noShowTitle', { defaultValue: 'Pickup hold deadline' })}
          </p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface)]">
            {t('account.orders.live.noShowDeadline', {
              defaultValue: 'Hold until: {{at}}',
              at: formatDate(noShowNotice.deadlineAt),
            })}
          </p>
          <p className="mt-1 m-0 text-sm text-[var(--color-on-surface-muted)]">{noShowNotice.fate}</p>
        </section>
      ) : null}
    </div>
  );
}
